export const METERS_PER_KILOMETER = 1000;
export const METERS_PER_MILE = 1609.344;
export const FEET_PER_METER = 3.28084;
export const FEET_PER_MILE = 5280;

export const normalizeDistanceUnit = (distanceUnit) => (
  distanceUnit === 'mi' ? 'mi' : 'km'
);

const translateUnit = (t, key, fallback) => (
  typeof t === 'function' ? t(key, fallback) : fallback
);

const formatNumber = (value, decimals = 1) => {
  const rounded = Number(value).toFixed(decimals);
  return rounded.replace(/\.0$/, '');
};

export const getDistanceUnitLabel = (distanceUnit = 'km', t = null) => {
  const unit = normalizeDistanceUnit(distanceUnit);
  return translateUnit(t, unit, unit);
};

export const getZeroDistanceLabel = (distanceUnit = 'km', t = null) => {
  const unit = normalizeDistanceUnit(distanceUnit);
  const zeroUnit = unit === 'mi' ? 'ft' : 'm';
  return `0 ${translateUnit(t, zeroUnit, zeroUnit)}`;
};

export const metersToDisplayDistance = (meters, distanceUnit = 'km', decimals = 1) => {
  const totalMeters = Number(meters);
  if (!Number.isFinite(totalMeters)) return null;
  const unit = normalizeDistanceUnit(distanceUnit);
  const divisor = unit === 'mi' ? METERS_PER_MILE : METERS_PER_KILOMETER;
  const factor = 10 ** decimals;
  return Math.round((totalMeters / divisor) * factor) / factor;
};

export const kilometersToDisplayDistance = (kilometers, distanceUnit = 'km', decimals = 1) => {
  const km = Number(kilometers);
  if (!Number.isFinite(km)) return null;
  return metersToDisplayDistance(km * METERS_PER_KILOMETER, distanceUnit, decimals);
};

export const displayDistanceToMeters = (distanceValue, distanceUnit = 'km') => {
  if (distanceValue === null || distanceValue === undefined || distanceValue === '') {
    return null;
  }
  const parsed = Number(distanceValue);
  if (!Number.isFinite(parsed)) return null;
  const unit = normalizeDistanceUnit(distanceUnit);
  return Math.round(parsed * (unit === 'mi' ? METERS_PER_MILE : METERS_PER_KILOMETER));
};

export const formatDistanceMeters = (
  meters,
  distanceUnit = 'km',
  t = null,
  options = {}
) => {
  const {
    compact = true,
    zeroLabel = '--'
  } = options;
  const totalMeters = Number(meters);
  if (!Number.isFinite(totalMeters) || totalMeters <= 0) return zeroLabel;

  const unit = normalizeDistanceUnit(distanceUnit);
  const separator = compact ? '' : ' ';

  if (unit === 'mi') {
    const feet = totalMeters * FEET_PER_METER;
    if (feet >= FEET_PER_MILE) {
      return `${formatNumber(feet / FEET_PER_MILE)}${separator}${translateUnit(t, 'mi', 'mi')}`;
    }
    return `${Math.round(feet)}${separator}${translateUnit(t, 'ft', 'ft')}`;
  }

  const roundedMeters = Math.round(totalMeters);
  if (roundedMeters >= METERS_PER_KILOMETER) {
    return `${formatNumber(roundedMeters / METERS_PER_KILOMETER)}${separator}${translateUnit(t, 'km', 'km')}`;
  }
  return `${roundedMeters}${separator}${translateUnit(t, 'm', 'm')}`;
};

export const formatDistanceKilometers = (
  kilometers,
  distanceUnit = 'km',
  t = null,
  options = {}
) => {
  const km = Number(kilometers);
  if (!Number.isFinite(km)) return options.zeroLabel ?? '--';
  return formatDistanceMeters(km * METERS_PER_KILOMETER, distanceUnit, t, options);
};
